<template>
  <div class="report-table">
    <div v-if="title" class="report-table__title">{{ title }}</div>

    <div v-if="!columns.length" class="report-table__empty">
      {{ $t("reports.noColumns") }}
    </div>

    <div v-else class="report-table__scroll">
      <table class="report-table__table">
        <thead>
          <tr>
            <th
              v-for="column in columns"
              :key="column.key"
              :class="headerClass(column)"
              :style="column.width ? { minWidth: column.width + 'px' } : null"
              @click="onHeaderClick(column)"
            >
              <span>{{ label(column) }}</span>
              <v-icon v-if="sortIcon(column)" small>{{ sortIcon(column) }}</v-icon>
              <div v-if="column.note_ar" class="report-table__note">
                {{ column.note_ar }}
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!rows.length">
            <td :colspan="columns.length" class="report-table__empty">
              {{ $t("reports.noRows") }}
            </td>
          </tr>
          <tr v-for="(row, index) in rows" :key="index">
            <td
              v-for="column in columns"
              :key="column.key"
              :class="cellClass(column)"
            >
              {{ reportCellText(column, row[column.key]) }}
            </td>
          </tr>
        </tbody>
        <tfoot v-if="hasTotals">
          <tr>
            <td
              v-for="(column, index) in columns"
              :key="column.key"
              :class="cellClass(column)"
            >
              <template v-if="index === 0">{{ $t("reports.total") }}</template>
              <template v-else-if="hasTotal(column)">
                {{ reportCellText(column, totals[column.key]) }}
              </template>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script>
import reportExport from "@/mixins/reportExport";

/*
 * The one table that renders every report (requirements doc 2026-09-09).
 *
 * Columns, alignment, units and totals all arrive as server metadata, so this
 * component knows nothing about any particular report.
 *
 * Sorting is emitted upward rather than done here: it is a server round trip
 * on purpose, because a paginated report can only be sorted correctly by the
 * database. Only columns the server marked sortable are clickable -- the
 * engine additionally falls back safely on an unknown sort key, so a stale
 * client can no longer 500 the request and leave the table spinning.
 */
export default {
  name: "ReportTable",
  mixins: [reportExport],
  props: {
    title: { type: String, default: "" },
    columns: { type: Array, default: () => [] },
    rows: { type: Array, default: () => [] },
    totals: { type: Object, default: () => ({}) },
    sortBy: { type: String, default: "" },
    descending: { type: Boolean, default: false },
  },
  computed: {
    hasTotals() {
      return Object.keys(this.totals || {}).length > 0;
    },
  },
  methods: {
    label(column) {
      return this.reportColumnLabel(column);
    },
    hasTotal(column) {
      return Object.prototype.hasOwnProperty.call(this.totals || {}, column.key);
    },
    headerClass(column) {
      return {
        "report-table__th--end": column.align === "end",
        "report-table__th--sortable": column.sortable,
      };
    },
    cellClass(column) {
      return {
        "report-table__td--end": column.align === "end",
        // Account numbers and plate numbers must stay left-to-right inside an
        // otherwise right-to-left table, or the digits reorder on screen.
        "report-table__td--ltr": column.unit === "code",
      };
    },
    sortIcon(column) {
      if (!column.sortable || this.sortBy !== column.key) {
        return null;
      }
      return this.descending ? "arrow_downward" : "arrow_upward";
    },
    onHeaderClick(column) {
      if (!column.sortable) {
        return;
      }
      this.$emit("sort", {
        sortBy: column.key,
        descending: this.sortBy === column.key ? !this.descending : false,
      });
    },
  },
};
</script>

<style scoped>
.report-table__title {
  font-size: 14px;
  font-weight: 700;
  padding: 10px 12px 6px;
}
/* Wide reports scroll inside their own box; the page never scrolls sideways. */
.report-table__scroll {
  overflow-x: auto;
  width: 100%;
}
.report-table__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.report-table__table th,
.report-table__table td {
  padding: 6px 10px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  white-space: nowrap;
}
.report-table__table thead th {
  font-weight: 700;
  background: #fafafa;
  position: sticky;
  top: 0;
  z-index: 1;
  vertical-align: bottom;
}
.report-table__th--sortable {
  cursor: pointer;
  user-select: none;
}
.report-table__th--end,
.report-table__td--end {
  text-align: end;
}
.report-table__td--ltr {
  direction: ltr;
  text-align: end;
}
.report-table__note {
  font-size: 10px;
  font-weight: 400;
  opacity: 0.6;
  white-space: normal;
  max-width: 200px;
}
.report-table__table tfoot td {
  font-weight: 700;
  background: #fafafa;
  border-top: 2px solid rgba(0, 0, 0, 0.2);
}
.report-table__empty {
  padding: 24px 12px;
  text-align: center;
  opacity: 0.6;
}
/* Printing is one of the page's three exports. On paper there is nothing to
   scroll, so a wide report has to be allowed to break across pages instead of
   being cut off at the edge of its scroll box, and a sticky header printed at
   a fixed offset lands on top of the first rows. */
@media print {
  .report-table__scroll {
    overflow: visible;
  }
  .report-table__table thead th {
    position: static;
  }
}
</style>
